import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header';
import { useDispatch, useSelector } from 'react-redux';
import { getAdminDashboardCount, getadminSalesPersonWeekOverview, getadminWeekOverview, getAllSalesOrders, getUsersList, toggleUserActive } from '../../redux/reduxSlices/adminSlice';
import { assignOrder, getAllOrders } from '../../redux/reduxSlices/ordersSlice';
import { CheckCircle, Clock, FileCheck, FileText, Filter, Loader, Music, Music2, Package, Plus, Search, Truck, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { getMusicCreators } from '../../redux/reduxSlices/userProfileSlice';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell
} from 'recharts';

const IndividualSalesRecord = ({ user, onLogout }) => {
    const {
        adminLoading,
        adminFetchError,
        adminSalesPersonWeekOverviewResponse,
        usersListResponse,
        toggleUserResponse,
        salesOrdersData,
    } = useSelector((state) => state.admin);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const { salesId } = useParams()
    
    // const {
    //     loading,
    //     fetchError,
    //     salesOrdersData,
    // } = useSelector((state) => state.orders);

    const { musicCreators } = useSelector((state) => state.profile);

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(getadminSalesPersonWeekOverview(salesId))
        dispatch(getUsersList())
        dispatch(getAllSalesOrders(salesId));
        dispatch(getMusicCreators());
    }, [])

    // Format data for Recharts
    const getChartData = () => {
        if (!adminSalesPersonWeekOverviewResponse?.days) return [];

        return adminSalesPersonWeekOverviewResponse.days.map(day => ({
            name: formatDate(day.day),
            fullDate: day.day,
            weekday: getWeekday(day.day),
            Received: day.total_received || 0,
            Completed: day.total_completed || 0,
            // You can add more data if needed
            Pending: (day.total_received || 0) - (day.total_completed || 0)
        }));
    };

    // Format tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                    <p className="font-semibold text-gray-800">{data.fullDate}</p>
                    <p className="text-sm text-gray-600 mb-2">{data.weekday}</p>
                    {payload.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-sm text-gray-600">{entry.dataKey}:</span>
                            </div>
                            <span className="font-semibold text-gray-800">{entry.value}</span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    const filteredOrders = (salesOrdersData.orders || []).filter((order) => {
        const search = searchTerm.toLowerCase();

        const companyName = order.company_name?.toLowerCase().trim() || "";
        const contactPerson = order.contact_person?.toLowerCase().trim() || "";
        const orderId = order.order_id?.toLowerCase().trim() || "";
        const email = order.email?.toLowerCase().trim() || "";

        const matchesSearch =
            companyName.includes(search) ||
            contactPerson.includes(search) ||
            orderId.includes(search) ||
            email.includes(search);

        const matchesStatus =
            filterStatus === "all" || order.status === filterStatus;

        return matchesSearch && matchesStatus;
    });


    // Simple date formatter
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const month = date.toLocaleString('default', { month: 'short' });
        const day = date.getDate();
        return `${month} ${day}`;
    };

    // Get weekday
    const getWeekday = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('default', { weekday: 'short' });
    };

    const getAssignedToName = (id) => {
        if (!id || !usersListResponse?.music_creators) return "-";

        const user = usersListResponse.music_creators.find(
            (user) => user.user_pk === id
        );

        if (user) {
            return `${user.first_name} ${user.last_name}`;
        }

        return "-";
    };

    const assignTask = async (orderId, assigned_to) => {
        try {
            const payload = {
                order_id: orderId,
                user_pk: assigned_to
            };
            const response = await dispatch(assignOrder(payload)).unwrap();
            if (response.success) {
                await dispatch(getAllOrders());
                toast.success("Task assigned successfully");
            }
        } catch (error) {
            console.log("error", error);
            toast.error(error?.message || "Failed to assign task.");
        }
    };

    const statusConfig = [
        { value: "pending", label: "Pending", color: "bg-yellow-500", gradient: "from-yellow-400 to-yellow-600", icon: Clock },
        { value: "in_progress", label: "In Progress", color: "bg-blue-500", gradient: "from-blue-400 to-blue-600", icon: Music },
        { value: "lyrics_approval_pending", label: "Lyrics Approval Pending", color: "bg-orange-500", gradient: "from-orange-400 to-orange-600", icon: FileText },
        { value: "lyrics_approved", label: "Lyrics Approved", color: "bg-purple-500", gradient: "from-purple-400 to-purple-600", icon: FileCheck },
        { value: "music_approval_pending", label: "Music Approval Pending", color: "bg-indigo-500", gradient: "from-indigo-400 to-indigo-600", icon: Music2 },
        { value: "music_approved", label: "Music Approved", color: "bg-teal-500", gradient: "from-teal-400 to-teal-600", icon: CheckCircle },
        { value: "delivered", label: "Delivered", color: "bg-green-500", gradient: "from-green-400 to-green-600", icon: Truck }
    ];

    const StatusBadge = ({ status }) => {
        const config = statusConfig.find(s => s.value === status);
        const Icon = config?.icon || Clock;

        return (
            <div className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${config?.color ? `bg-${config.color.split('-')[1]}-100 text-${config.color.split('-')[1]}-800` : 'bg-gray-100 text-gray-800'
                }`}>
                <Icon className="w-3 h-3" />
                {config?.label || status}
            </div>
        );
    };


    const navigate = useNavigate()

    const togglePersonStatus = async (person) => {
        try {
            const payload = {
                "user_pk": person.user_pk,
                "user_type": person.user_type,
                "is_active": !person.is_active
            }
            const response = await dispatch(toggleUserActive(payload)).unwrap()
            if (response.success) {
                await dispatch(getUsersList())
                toast.success("Status changed successfully!")
            }
        } catch (error) {
            console.log("error", error)
            toast.error("Something went wrong!");
        }
    };

    const handleViewMoreSales = () => {
        navigate("/admin-sales-persons-data")
    };

    const handleViewMoreMusic = () => {
        navigate("/admin-music-creator-data")
    };

    const handleViewMoreOrders = () => {
        navigate("/admin-order-data")
    };

    // Chart data
    const chartData = getChartData();

    if (adminLoading) {
        return <div className="text-center py-8">
            <Loader className="w-8 h-8 text-orange-500 animate-spin mx-auto" />
            {/* <p className="text-gray-600 text-sm mt-2">Loading order details...</p> */}
        </div>;
    }
    return (
        <>
            <Header user={user} onLogout={onLogout} />
            <div className="min-h-screen py-4 md:px-7 max-w-[1800px] mx-auto ">

                {/* <button className="inline-flex items-center px-3 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 mt-2"
          onClick={() => navigate('/create-collage')} ><Plus className="w-4 h-4" />Create Collage
        </button> */}

                {/* Stats Cards Grid */}


                {/* Recharts Bar Graph */}
                {adminSalesPersonWeekOverviewResponse?.days && (
                    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                        <h2 className="text-xl font-bold text-gray-800 mb-6">Last 7 Days Overview</h2>

                        <div className="h-80 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={chartData}
                                    margin={{
                                        top: 20,
                                        right: 30,
                                        left: 20,
                                        bottom: 60,
                                    }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#f0f0f0"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#6b7280', fontSize: 12 }}
                                        interval={0}
                                    >
                                        <label
                                            value="Date"
                                            offset={-20}
                                            position="insideBottom"
                                            style={{ textAnchor: 'middle', fill: '#6b7280' }}
                                        />
                                    </XAxis>
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#6b7280', fontSize: 12 }}
                                        label={{
                                            value: 'Number of Orders',
                                            angle: -90,
                                            position: 'insideLeft',
                                            style: { textAnchor: 'middle', fill: '#6b7280' }
                                        }}
                                    />
                                    <Tooltip
                                        content={<CustomTooltip />}
                                        cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                                    />
                                    <Legend
                                        verticalAlign="top"
                                        height={36}
                                        wrapperStyle={{ paddingBottom: '20px' }}
                                    />
                                    <Bar
                                        dataKey="Received"
                                        name="Orders Received"
                                        fill="#3b82f6"
                                        radius={[4, 4, 0, 0]}
                                        barSize={30}
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill="#3b82f6"
                                                className="hover:opacity-80 transition-opacity"
                                            />
                                        ))}
                                    </Bar>
                                    <Bar
                                        dataKey="Completed"
                                        name="Orders Completed"
                                        fill="#10b981"
                                        radius={[4, 4, 0, 0]}
                                        barSize={30}
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill="#10b981"
                                                className="hover:opacity-80 transition-opacity"
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Summary */}
                        <div className="grid grid-cols-3 gap-4 mt-6">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <div className="text-sm text-gray-600 mb-1">Total Received</div>
                                <div className="text-2xl font-bold text-blue-600">
                                    {adminSalesPersonWeekOverviewResponse.totals_last_7_days.received}
                                </div>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg">
                                <div className="text-sm text-gray-600 mb-1">Total Completed</div>
                                <div className="text-2xl font-bold text-green-600">
                                    {adminSalesPersonWeekOverviewResponse.totals_last_7_days.completed}
                                </div>
                            </div>
                            <div className="bg-yellow-50 p-4 rounded-lg">
                                <div className="text-sm text-gray-600 mb-1">Still Pending</div>
                                <div className="text-2xl font-bold text-yellow-600">
                                    {(adminSalesPersonWeekOverviewResponse.totals_last_7_days.received - adminSalesPersonWeekOverviewResponse.totals_last_7_days.completed) > 0 ?
                                        (adminSalesPersonWeekOverviewResponse.totals_last_7_days.received - adminSalesPersonWeekOverviewResponse.totals_last_7_days.completed) : 0}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Order Details Card */}
                <div className="bg-white rounded-lg shadow-md p-6">

                    {/* Search and Filter Section - Compact */}
                    <div className="bg-white rounded-lg p-4 shadow border border-gray-200 mb-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Client Orders</h2>
                                <p className="text-sm text-gray-600">{filteredOrders.length} orders found</p>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search orders..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full py-2 pl-10 pr-3 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4 text-gray-400" />
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="py-2 px-3 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-w-[160px]"
                                >
                                    <option value="all">All Status</option>
                                    {statusConfig.map((status) => (
                                        <option key={status.value} value={status.value}>
                                            {status.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    {/* <SimpleArrowTimeline /> */}


                    {/* Orders Table Section - Main Focus */}
                    <div className="bg-white rounded-lg shadow border border-gray-200">
                        {adminLoading ? (
                            <div className="p-8 text-center">
                                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg mb-3">
                                    <Package className="w-6 h-6 text-white animate-pulse" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                    Loading orders...
                                </h3>
                                <p className="text-gray-600 text-sm">Please wait</p>
                            </div>
                        ) : filteredOrders.length === 0 ? (
                            <div className="text-center py-12 px-8">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-lg mb-4">
                                    <Users className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 mb-2">
                                    No orders found
                                </h3>
                                <p className="text-gray-600 text-sm">Try adjusting your search or filters</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                #
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Order ID
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Company
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Contact
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Email
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Assign to
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredOrders.map((order, index) => (
                                            <tr key={order.order_id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    {index + 1}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {order.order_id || "-"}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="text-sm font-medium text-gray-900 max-w-[120px] truncate">
                                                        {order.company_name || order.brand_company_name || "-"}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="text-sm text-gray-900 max-w-[100px] truncate" title={order.contact_person || "-"}>
                                                        {(order.contact_person && order.contact_person.length > 20)
                                                            ? `${order.contact_person.substring(0, 20)}...`
                                                            : (order.contact_person || "-")}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {(order.mobile_number && order.mobile_number.length > 20)
                                                            ? `${order.mobile_number.substring(0, 20)}...`
                                                            : (order.mobile_number || "-")}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700 max-w-[150px] truncate" title={order.email || "-"}>
                                                    {order.email || "-"}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {getAssignedToName(order.assigned_to)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <StatusBadge status={order.status.status} />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => navigate(`/my-orders/${order.order_id}`)}
                                                            className="inline-flex items-center px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                                                        >
                                                            View
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        #
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Order ID
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Company
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Contact
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Assigned to
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredOrders?.map((order, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm text-gray-700">
                                            {index + 1}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-sm font-medium text-gray-900">
                                                {order.order_id || "-"}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-sm font-medium text-gray-900 max-w-[120px] truncate">
                                                {order.company_name || order.brand_company_name || "-"}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-sm text-gray-900 max-w-[100px] truncate" title={order.contact_person || "-"}>
                                                {(order.contact_person && order.contact_person.length > 20)
                                                    ? `${order.contact_person.substring(0, 20)}...`
                                                    : (order.contact_person || "-")}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {(order.mobile_number && order.mobile_number.length > 20)
                                                    ? `${order.mobile_number.substring(0, 20)}...`
                                                    : (order.mobile_number || "-")}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 max-w-[150px] truncate" title={order.email || "-"}>
                                            {order.email || "-"}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 max-w-[150px] truncate" title={order.assigned_to || "-"}>
                                            {getAssignedToName(order.assigned_to)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge status={order.status} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                onClick={() => navigate(`/my-orders/${order.order_id}`)}
                                                className="inline-flex items-center px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div> */}
                </div>
            </div>
        </>
    )
}

export default IndividualSalesRecord